import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Pricing } from "@/components/Pricing";
import { PublicFooter, KIND_PATH, usePublishedPages, pageLabel, pagePath } from "@/components/PublicFooter";
import { ShareButtons } from "@/components/ShareButtons";
import { ChecklistOffer } from "@/components/ChecklistOffer";
import { usePageSeo } from "@/components/Seo";

const KICKER = { city: "Local feasibility studies", industry: "Industry feasibility studies", article: "Resources", combo: "Local feasibility studies" };

export const RichText = ({ text, testId }) => (
  <div className="space-y-4 text-slate-300 leading-relaxed" data-testid={testId}>
    {text.split(/\n\s*\n/).flatMap((block, i) => {
      const groups = [];
      block.split("\n").map((l) => l.trim()).filter(Boolean).forEach((l) => {
        const bullet = l.startsWith("- ");
        const last = groups[groups.length - 1];
        if (last && last.bullet === bullet) last.lines.push(bullet ? l.slice(2) : l);
        else groups.push({ bullet, lines: [bullet ? l.slice(2) : l] });
      });
      return groups.map((g, j) => (g.bullet
        ? <ul key={`${i}-${j}`} className="list-disc pl-5 space-y-1.5 marker:text-slate-500">{g.lines.map((l, k) => <li key={k}>{l}</li>)}</ul>
        : <p key={`${i}-${j}`}>{g.lines.join(" ")}</p>));
    })}
  </div>
);

const schema = (p, url) => {
  const org = { "@id": window.location.origin + "/#business" };
  const faq = p.faq?.length ? [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] : [];
  const trail = [["Home", "/"], [{ city: "Locations", industry: "Industries", article: "Resources", combo: "Locations" }[p.kind], KIND_PATH[p.kind]],
    ...(p.kind === "combo" ? [[`${p.city}, ${p.state}`, `/locations/${p.city_slug}`]] : []), [p.title, url.replace(window.location.origin, "")]];
  const crumbs = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: trail.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: window.location.origin + path })) };
  if (p.kind === "article") {
    return [{ "@context": "https://schema.org", "@type": "Article", headline: p.title, description: p.meta_description, datePublished: p.published_at, dateModified: p.updated_at,
      mainEntityOfPage: url, image: window.location.origin + "/og-image.jpg", author: { "@type": "Organization", ...org }, publisher: { "@type": "Organization", ...org } }, crumbs, ...faq];
  }
  const area = p.kind === "city" || p.kind === "combo" ? { "@type": "City", name: `${p.city}, ${p.state}` } : { "@type": "Country", name: "United States" };
  return [{ "@context": "https://schema.org", "@type": "Service", name: p.title, serviceType: p.kind === "combo" ? `${p.industry} feasibility study` : "Feasibility study", description: p.meta_description, provider: org, areaServed: area, url }, crumbs, ...faq];
};

const LinkGroup = ({ title, pages, testId, labelFn = pageLabel }) => (!pages.length ? null : (
    <div className="mb-10" data-testid={testId}>
    <h2 className="font-display text-2xl font-bold mb-4">{title}</h2>
    <div className="flex flex-wrap gap-x-6 gap-y-2">{pages.map((p) => <Link key={p.id} to={pagePath(p)} className="text-sm text-slate-300 underline decoration-slate-700 underline-offset-4 hover:text-slate-50 hover:decoration-slate-300 transition-colors">{labelFn(p)}</Link>)}</div>
  </div>
));

const Related = ({ page }) => {
  const all = usePublishedPages();
  const combos = all.filter((p) => p.kind === "combo" && p.id !== page.id);
  const same = all.filter((p) => p.kind === page.kind && p.id !== page.id);
  const groups = {
    city: [[`Industries we cover in ${page.city}`, combos.filter((p) => p.city_slug === page.slug), (p) => p.industry], ["Other locations we serve", same.slice(0, 12)]],
    industry: [[`${page.title.replace(/^Feasibility Studies for /i, "")} studies by city`, combos.filter((p) => p.industry_slug === page.slug), (p) => `${p.city}, ${p.state}`], ["Other industries", same]],
    combo: [[`More industries in ${page.city}`, combos.filter((p) => p.city_slug === page.city_slug), (p) => p.industry], [`${page.industry} in other cities`, combos.filter((p) => p.slug === page.slug).slice(0, 12), (p) => `${p.city}, ${p.state}`]],
    article: [["More guides", same.slice(0, 8)]],
  }[page.kind];
  if (!groups.some(([, pages]) => pages.length)) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="site-page-related">
      {groups.map(([title, pages, fn], i) => <LinkGroup key={i} title={title} pages={pages} labelFn={fn} testId={`site-page-related-${i}`} />)}
    </section>
  );
};

export default function SitePage({ kind }) {
  const { slug, citySlug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  const path = kind === "combo" ? `/locations/${citySlug}/${slug}` : `${KIND_PATH[kind]}/${slug}`;
  useEffect(() => {
    setPage(null); setMissing(false);
    api.get(kind === "combo" ? `/public/site-pages/combo/${citySlug}/${slug}` : `/public/site-pages/${kind}/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true));
  }, [kind, slug, citySlug]);
  usePageSeo(page ? { title: page.title, description: page.meta_description, path, type: kind === "article" ? "article" : "website", jsonLd: schema(page, window.location.origin + path) } : missing ? { title: "Page not found", path, noindex: true } : null);
  if (missing) return <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="site-page-missing"><h1 className="font-display text-3xl font-bold mb-4">Page not found</h1><Link to="/" className="text-emerald-400 underline">Back to home</Link></div>;
  if (!page) return <div className="py-32 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  return (
    <div data-testid="site-page" className="public-site">
      <section className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 fade-up">
          <nav className="text-xs text-slate-500 mb-8" aria-label="Breadcrumb" data-testid="site-page-breadcrumb"><Link to="/" className="hover:text-slate-300">Home</Link> / <Link to={KIND_PATH[kind]} className="hover:text-slate-300">{KICKER[kind]}</Link>{kind === "combo" && <> / <Link to={`/locations/${page.city_slug}`} className="hover:text-slate-300">{page.city}, {page.state}</Link></>}</nav>
          <div className="max-w-4xl">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-5">{KICKER[kind]}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 text-slate-50" data-testid="site-page-h1">{page.h1 || page.title}</h1>
            <p className="text-slate-300 text-base md:text-lg max-w-3xl mb-9 leading-relaxed" data-testid="site-page-intro">{page.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="site-page-cta" className="h-12 px-7 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold"><a href="#pricing">Request a study<ArrowRight className="h-4 w-4 ml-2" /></a></Button>
              <Button asChild variant="outline" className="h-12 px-7 rounded-full border-slate-600 bg-transparent hover:bg-slate-800"><Link to="/feasibility-studies" data-testid="site-page-loan-link">Studies for SBA and bank loans</Link></Button>
            </div>
            {kind === "article" && <div className="mt-8"><ShareButtons title={page.title} path={path} testId="share-top" /></div>}
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-12">
        <aside className="hidden lg:block lg:col-span-3" data-testid="site-page-toc">
          <div className="sticky top-24">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">On this page</p>
            <ol className="space-y-3 text-sm border-l border-slate-800">
              {page.sections.map((s, i) => <li key={i}><a href={`#s${i}`} className="block pl-4 -ml-px border-l border-transparent text-slate-400 hover:text-slate-100 hover:border-slate-400 transition-colors">{s.heading}</a></li>)}
              {page.faq?.length > 0 && <li><a href="#faq" className="block pl-4 -ml-px border-l border-transparent text-slate-400 hover:text-slate-100 hover:border-slate-400 transition-colors">Questions</a></li>}
            </ol>
          </div>
        </aside>
        <article className="lg:col-span-8 lg:col-start-5 space-y-14 max-w-3xl">
          {page.sections.map((s, i) => (
            <Fragment key={i}>
              <section id={`s${i}`} className="scroll-mt-24" data-testid={`site-page-section-${i}`}>
                <h2 className="font-display text-3xl font-bold tracking-tight mb-5 text-slate-50">{s.heading}</h2>
                <RichText text={s.body} />
              </section>
              {kind === "article" && i === Math.min(1, page.sections.length - 1) && <ChecklistOffer page={page.slug} />}
              {(kind === "industry" || kind === "combo") && i === Math.min(1, page.sections.length - 1) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="industry-magnets">
                  <ChecklistOffer magnet="costs" page={page.slug} pageKind={kind} citySlug={page.city_slug || ""} compact />
                  <ChecklistOffer magnet="fdd" page={page.slug} pageKind={kind} citySlug={page.city_slug || ""} compact />
                </div>
              )}
            </Fragment>
          ))}
          {page.faq?.length > 0 && (
            <section id="faq" className="scroll-mt-24" data-testid="site-page-faq">
              <h2 className="font-display text-3xl font-bold tracking-tight mb-5 text-slate-50">Questions clients ask</h2>
              <Accordion type="single" collapsible className="border-t border-slate-800">
                {page.faq.map((f, i) => <AccordionItem key={i} value={`f${i}`} className="border-slate-800"><AccordionTrigger className="text-left">{f.q}</AccordionTrigger><AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent></AccordionItem>)}
              </Accordion>
            </section>
          )}
          {kind === "article" && <div className="border-t border-slate-800 pt-8"><p className="text-sm text-slate-400 mb-3">If a client or colleague would benefit from this guide, you may share it below.</p><ShareButtons title={page.title} path={path} testId="share-bottom" /></div>}
        </article>
      </div>
      <Pricing />
      <Related page={page} />
      <PublicFooter />
    </div>
  );
}
